import { createContext, useContext, useMemo, useState } from "react";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { FileResponseDto, FileTagResponseDto } from "~/gen/api/securefile";
import { useFileCataAmbientDataContext } from "./FileCataAmbientDataProvider";


export interface FileCataInfoEditContextProps {
  states: {
      classification: "" | SecondaryClassificationResponseDto | null;
      tagsToDelSet: Set<number>;
      tagsToAddSet: Set<number>;
      tagsToAdd: FileTagResponseDto[];
  };
  getEffectClssItem: () => SecondaryClassificationResponseDto | '';
  getEffectTags: () => (FileTagResponseDto | undefined)[];
  getDisplayTags: () => (FileTagResponseDto | undefined)[];
  setClssItem: (item: SecondaryClassificationResponseDto | '') => void;
  addTag: (t: FileTagResponseDto) => void;
  delTag: (t: FileTagResponseDto | number) => void;
  dirty: boolean;
}

export const FileCataInfoEditContext = createContext<FileCataInfoEditContextProps>({} as any)

export const useFileCataInfoEditContext = () => useContext(FileCataInfoEditContext);

export const useFileCataInfoEditController = (record: FileResponseDto): FileCataInfoEditContextProps => {
  const { classifications, tags } = useFileCataAmbientDataContext()
  const [prevClssItem, prevTags] = useMemo(() => {
    const clss = classifications.find(c => c.secondaryClassificationId === record.secondaryClassificationId)
    const ts = (record.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x)
    return [clss, ts] as const
  }, [record, classifications, tags])
  
  const [states, setStates] = useState({
    classification: null as SecondaryClassificationResponseDto|null|'',
    tagsToDelSet: new Set<number>(),
    tagsToAddSet: new Set<number>(),
    tagsToAdd: [] as FileTagResponseDto[],
  })

  const getEffectClssItem = () : SecondaryClassificationResponseDto|'' => {
    return states.classification ?? prevClssItem ?? ''
  }
  const getEffectTags = () => {
    return [...prevTags.filter(t => !states.tagsToDelSet.has(t?.tagId!)), ...states.tagsToAdd]
  }
  const getDisplayTags = () => {
    return [...prevTags, ...states.tagsToAdd]
  }
  const setClssItem = (item: SecondaryClassificationResponseDto|'') => {
    setStates(v => {
      if (item === states.classification) { return v }
      return (item === prevClssItem
        ? {...v, classification: null}
        : {...v, classification: item}
      )
    })
  }
  const addTag = (t: FileTagResponseDto) => {
    setStates(v => {
      const tid = t.tagId!
      if (v.tagsToDelSet.has(tid)) {
        v.tagsToDelSet.delete(tid)
        return {...v}
      }
      if (v.tagsToAddSet.has(tid) || prevTags.some(x => x?.tagId === tid)) {
        return v
      } else {
        v.tagsToAdd.push(t)
        v.tagsToAddSet.add(tid)
        return {...v}
      }
    })
  }
  const delTag = (t: FileTagResponseDto|number) => {
    setStates(v => {
      const tid = typeof t === 'number' ? t : t.tagId!
      if (v.tagsToAddSet.has(tid)) {
        v.tagsToAddSet.delete(tid)
        const i = v.tagsToAdd.findIndex(x => x.tagId === tid)
        if (i > -1) { v.tagsToAdd.splice(i, 1) }
        return {...v}
      }
      if (v.tagsToDelSet.has(tid)) { return v }
      v.tagsToDelSet.add(tid)
      return {...v}
    })
  }
  return {
    states,
    getEffectClssItem,
    getEffectTags,
    getDisplayTags,
    setClssItem,
    addTag,
    delTag,
    dirty: 
         states.classification !== null 
      || states.tagsToAddSet.size > 0 
      || states.tagsToDelSet.size > 0
  };
}

